/**
 * @fileoverview The single row of the student matrix: sticky name cell, per-lesson cells (self / teacher /
 * one deliverable per activity) and the Final Assessment pair. Clicks open the student's own drawers.
 */

import { Fragment } from 'react'
import { Avatar, Group, Loader, Stack, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import type { StudentTrackerActivity, StudentTrackerFinal, StudentTrackerLesson, StudentTrackerMatrix } from '../types'
import { deliverablePlan, finalSelfAssessmentPlan, selfAssessmentPlan, teacherAssessmentPlan } from './cellActions'
import type { CellAction, CellPlan } from './cellActions'
import { formatDate } from './formatDate'
import { cellStyle, stickyBodyStyle } from './matrixStyles'
import { StudentTrackerIconCell } from './StudentTrackerIconCell'

const AVATAR_SIZE = 28
const TOOLTIP_LINE_GAP = 2

export interface MatrixRowHandlers {
  onOpenDeliverable: (lesson: StudentTrackerLesson, activityId: string) => void
  onOpenSelfAssessment: (lesson: StudentTrackerLesson) => void
  onOpenTeacherAssessment: (lesson: StudentTrackerLesson) => void
  onOpenFinal: () => void
}

interface StudentTrackerMatrixRowProps extends MatrixRowHandlers {
  matrix: StudentTrackerMatrix
  editable: boolean
  today: string
  /** Cells spanned by the processing loader (every lesson sub-column + final pair). */
  totalColumns: number
}

type Translate = (key: string, options?: object) => string

function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? parts[parts.length - 1][0] : ''
  return `${first}${last}`.toUpperCase() || '?'
}

/** Status label + the action hint ("Click to submit") or the reason the cell is locked. */
function assessmentTooltip(tr: Translate, statusKey: string, plan: CellPlan): string {
  const parts = [tr(`status.${statusKey}`)]
  if (plan.disabledReason) parts.push(tr(`table.${plan.disabledReason}`))
  else if (plan.action) parts.push(tr(`cell.${plan.action}_hint`))
  return parts.join(' · ')
}

function deliverableAction(activity: StudentTrackerActivity, plan: CellPlan): CellAction | null {
  if (!plan.action) return null
  return activity.completed ? 'edit' : 'submit'
}

function DeliverableTooltip({
  lesson,
  activity,
  action,
  language,
}: {
  lesson: StudentTrackerLesson
  activity: StudentTrackerActivity
  action: CellAction | null
  language: string
}) {
  const { t } = useTranslation()
  const tr: Translate = (key, options) => t(`student_task_tracker.${key}`, options)
  const delivery = activity.delivery
  const details: string[] = []
  if (activity.deadline) details.push(tr('table.due', { date: formatDate(activity.deadline, language) }))
  if (delivery?.late) details.push(tr('table.late'))
  if (delivery?.uploadedByTeacher) {
    details.push(
      delivery.uploadedByName
        ? tr('table.uploaded_by_teacher', { name: delivery.uploadedByName })
        : tr('table.uploaded_by_teacher_unknown'),
    )
  }
  return (
    <Stack spacing={TOOLTIP_LINE_GAP} align='flex-start'>
      <Text size='sm' weight={600}>
        {tr(activity.completed ? 'cell.deliverable_submitted' : 'cell.deliverable_pending')}
      </Text>
      {lesson.deliverableActivities.length > 1 && <Text size='xs'>{activity.title}</Text>}
      {details.length > 0 && (
        <Text size='xs' sx={theme => ({ color: theme.colors.gray[4] })}>
          {details.join(' · ')}
        </Text>
      )}
      {delivery?.note && <Text size='xs'>{tr('table.teacher_note', { note: delivery.note })}</Text>}
      {action && <Text size='xs'>{tr(`cell.${action}_hint`)}</Text>}
    </Stack>
  )
}

interface FinalCellsProps {
  final: StudentTrackerFinal
  editable: boolean
  onOpenFinal: () => void
}

function FinalCells({ final, editable, onOpenFinal }: FinalCellsProps) {
  const { t } = useTranslation()
  const tr: Translate = (key, options) => t(`student_task_tracker.${key}`, options)
  if (!final.present) {
    return (
      <>
        <td style={cellStyle}>
          <StudentTrackerIconCell status='pending' enabled={false} tooltip='' />
        </td>
        <td style={cellStyle}>
          <StudentTrackerIconCell status='pending' enabled={false} tooltip='' />
        </td>
      </>
    )
  }
  const plan = finalSelfAssessmentPlan(final, editable)
  const notStarted = !final.enabled
  const selfTooltip = notStarted
    ? tr('table.final_not_started')
    : assessmentTooltip(tr, `self_${final.selfStatus}`, plan)
  return (
    <>
      <td style={cellStyle}>
        <StudentTrackerIconCell
          status={final.selfStatus}
          tooltip={selfTooltip}
          onClick={plan.action ? onOpenFinal : undefined}
          disabled={notStarted}
        />
      </td>
      <td style={cellStyle}>
        <StudentTrackerIconCell
          status={final.teacherStatus}
          tooltip={notStarted ? tr('table.final_not_started') : tr(`status.teacher_${final.teacherStatus}`)}
          disabled={notStarted}
        />
      </td>
    </>
  )
}

export function StudentTrackerMatrixRow(props: StudentTrackerMatrixRowProps) {
  const { matrix, editable, today, totalColumns } = props
  const { onOpenDeliverable, onOpenSelfAssessment, onOpenTeacherAssessment, onOpenFinal } = props
  const { t, i18n } = useTranslation()
  const tr: Translate = (key, options) => t(`student_task_tracker.${key}`, options)

  const studentCell = (
    <td style={stickyBodyStyle}>
      <Group spacing='sm' noWrap>
        <Avatar size={AVATAR_SIZE} radius='xl' color='blue'>
          {initials(matrix.studentName)}
        </Avatar>
        <Text size='sm' weight={500} lineClamp={1}>
          {matrix.studentName || tr('table.student')}
        </Text>
      </Group>
    </td>
  )

  // The backend is still generating the assessment structure; the matrix polls until it fills.
  if (matrix.processing && !matrix.studentId) {
    return (
      <tr>
        {studentCell}
        <td colSpan={totalColumns} style={cellStyle}>
          <Group spacing='xs' position='center' noWrap>
            <Loader size='xs' />
            <Text size='xs' color='dimmed'>
              {tr('processing')}
            </Text>
          </Group>
        </td>
      </tr>
    )
  }

  return (
    <tr>
      {studentCell}
      {matrix.lessons.map(lesson => {
        const selfPlan = selfAssessmentPlan(lesson, editable, today)
        const teacherPlan = teacherAssessmentPlan(lesson)
        const delivPlan = deliverablePlan(lesson, editable)
        return (
          <Fragment key={lesson.id}>
            {lesson.hasAssessments && (
              <>
                <td style={cellStyle}>
                  <StudentTrackerIconCell
                    status={lesson.selfStatus}
                    tooltip={assessmentTooltip(tr, `self_${lesson.selfStatus}`, selfPlan)}
                    onClick={selfPlan.action ? () => onOpenSelfAssessment(lesson) : undefined}
                    disabled={selfPlan.disabled}
                  />
                </td>
                <td style={cellStyle}>
                  <StudentTrackerIconCell
                    status={lesson.teacherStatus}
                    tooltip={assessmentTooltip(tr, `teacher_${lesson.teacherStatus}`, teacherPlan)}
                    onClick={teacherPlan.action ? () => onOpenTeacherAssessment(lesson) : undefined}
                  />
                </td>
              </>
            )}
            {lesson.deliverableActivities.map(activity => {
              const action = deliverableAction(activity, delivPlan)
              return (
                <td key={activity.id} style={cellStyle}>
                  <StudentTrackerIconCell
                    status={activity.completed ? 'completed' : 'pending'}
                    tooltip={
                      <DeliverableTooltip
                        lesson={lesson}
                        activity={activity}
                        action={action}
                        language={i18n.language}
                      />
                    }
                    onClick={action ? () => onOpenDeliverable(lesson, activity.id) : undefined}
                    uploadedByTeacher={Boolean(activity.delivery?.uploadedByTeacher)}
                    multiline
                  />
                </td>
              )
            })}
          </Fragment>
        )
      })}
      <FinalCells final={matrix.final} editable={editable} onOpenFinal={onOpenFinal} />
    </tr>
  )
}
